import React, { useState, useEffect, useMemo } from 'react';
import { ALERT_TYPE, ShowToastMessage, useForm } from '@laramie/ui';
import {object, func, bool, string} from 'prop-types';
import {CloneObject, CompareDates} from '../../../utils/helpers';
import {getDateNow} from '../../../utils/convert';
import { useFormErrors } from '../../hooks/useFormErrors';
import { VALIDATION_MESSAGE } from '../../../consts/validationMessage';
import {useEntidad} from '../../hooks/useEntidad';
import {isEmptyString, isNull, isValidNumber} from '../../../utils/validator';
import {
    DatePickerCustom,
    InputNumber,
    InputPercent,
    InputEntidad,
    InputPersona,
    InputExpediente,
    InputTasa,
    InputSubTasa,
    Accordion,
    FormError,
    FormLabel,
    Modal
} from '../../common';
import SegmentedControl from '../../common/SegmentedControl';
import DataTaggerFormRedux from '../DataTaggerFormRedux';
import InputEjercicio from '../../common/InputEjercicio';
import {TIPS} from "../../../consts/tips";


const RecargoDescuentoModal = (props) => {

    //variables
    const entityInit = {
        id: 0,
        idCuenta: 0,
        idTipoRecargoDescuento: 0,
        idTasa: 0,
        idSubTasa: 0,
        idRubro: 0,
        fechaDesde: null,
        fechaHasta: null,
        fechaOtorgamiento: null,
        numeroSolicitud: '',
        porcentaje: 0,
        importe: 0,
        checkPorcentaje: false,
        checkImporte: false,
        idTipoPersona: 0,
        idPersona: 0,
        nombrePersona: '',
        numeroDocumento: '',
        idTipoDocumento: 0,
        numeroDDJJ: '',
        letraDDJJ: '',
        ejercicioDDJJ: '',
        numeroDecreto: '',
        letraDecreto: '',
        ejercicioDecreto: '',
        idExpediente: 0,
        detalleExpediente: ''
    };

    //hooks
    const [state, setState] = useState({
        entity: entityInit,
        idTipoTributo: 0,
        personas: [],
    });

    const [persona, setPersona] = useState({
        idTipoPersona: 0,
        nombrePersona: "",
        numeroDocumento: "",
        idTipoDocumento: 0
    });

    const [expediente, setExpediente] = useState({
        detalleExpediente: ""
    });

    const [listOthers, setListOthers] = useState([]);

    const [formValues, formHandle, , formSet] = useForm({
        idTipoRecargoDescuento: 0,
        idTasa: 0,
        idSubTasa: 0,
        idRubro: 0,
        fechaDesde: null,
        fechaHasta: null,
        fechaOtorgamiento: null,
        numeroSolicitud: '',
        porcentaje: 0,
        importe: 0,
        checkPorcentaje: true,
        checkImporte: false,
        idPersona: 0,
        numeroDDJJ: '',
        letraDDJJ: '',
        ejercicioDDJJ: '',
        numeroDecreto: '',
        letraDecreto: '',
        ejercicioDecreto: '',
        idExpediente: 0
    });

    const today = useMemo(() => getDateNow(), []);

    const { errorOf, validate } = useFormErrors(getErrors);

    const mount = () => {
        setState(prevState => {
            return {
                ...prevState,
                entity: props.data.entity,
                idTipoTributo: props.data.idTipoTributo,
                personas: props.data.personas ?? []
            };
        });
        formSet({
            idTipoRecargoDescuento: props.data.entity.idTipoRecargoDescuento,
            idTasa: props.data.entity.idTasa,
            idSubTasa: props.data.entity.idSubTasa,
            idRubro: props.data.entity.idRubro,
            fechaDesde: props.data.entity.fechaDesde,
            fechaHasta: props.data.entity.fechaHasta,
            fechaOtorgamiento: props.data.entity.fechaOtorgamiento,
            numeroSolicitud: props.data.entity.numeroSolicitud,
            porcentaje: props.data.entity.porcentaje,
            importe: props.data.entity.importe,
            checkPorcentaje: (props.data.entity.importe === 0),
            checkImporte: (props.data.entity.importe > 0),
            idPersona: props.data.entity.idPersona,
            numeroDDJJ: props.data.entity.numeroDDJJ,
            letraDDJJ: props.data.entity.letraDDJJ,
            ejercicioDDJJ: props.data.entity.ejercicioDDJJ,
            numeroDecreto: props.data.entity.numeroDecreto,
            letraDecreto: props.data.entity.letraDecreto,
            ejercicioDecreto: props.data.entity.ejercicioDecreto,
            idExpediente: props.data.entity.idExpediente
        });
        setPersona({
            idTipoPersona: props.data.entity.idTipoPersona,
            nombrePersona: props.data.entity.nombrePersona,
            numeroDocumento: props.data.entity.numeroDocumento,
            idTipoDocumento: props.data.entity.idTipoDocumento
        });
        setExpediente({
            detalleExpediente: props.data.entity.detalleExpediente
        });
        setListOthers(props.data.listAll.filter(f => f.id !== props.data.entity.id));
    }
    useEffect(mount, [props.data.entity]);

    const [, getRowEntidad] = useEntidad({
        entidades: ['TipoRecargoDescuento'],
        onLoaded: (entidades, isSuccess, error) => {
            if (isSuccess) {
                setState(prevState => ({...prevState}));
            } else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: 'TipoRecargoDescuento',
            timeout: 0
        }
    });

    //handles
    const handleClickAceptar = () => {
        if (!validate()) return;

        let row = CloneObject(state.entity);

        row.idTipoRecargoDescuento = parseInt(formValues.idTipoRecargoDescuento);
        row.idTasa = parseInt(formValues.idTasa);
        row.idSubTasa = parseInt(formValues.idSubTasa);
        row.idRubro = !isEmptyString(formValues.idRubro) ? parseInt(formValues.idRubro) : 0;
        row.fechaDesde = formValues.fechaDesde;
        row.fechaHasta = formValues.fechaHasta;
        row.fechaOtorgamiento = formValues.fechaOtorgamiento;
        row.numeroSolicitud = formValues.numeroSolicitud;
        row.porcentaje = formValues.porcentaje;
        row.importe = formValues.importe;
        row.idPersona = parseInt(formValues.idPersona);
        row.idTipoPersona = persona.idTipoPersona;
        row.nombrePersona = persona.nombrePersona;
        row.numeroDocumento = persona.numeroDocumento;
        row.idTipoDocumento = persona.idTipoDocumento;
        row.numeroDDJJ = formValues.numeroDDJJ;
        row.letraDDJJ = formValues.letraDDJJ;
        row.ejercicioDDJJ = formValues.ejercicioDDJJ;
        row.numeroDecreto = formValues.numeroDecreto;
        row.letraDecreto = formValues.letraDecreto;
        row.ejercicioDecreto = formValues.ejercicioDecreto;
        row.idExpediente = !isEmptyString(formValues.idExpediente) ? parseInt(formValues.idExpediente) : 0;
        row.detalleExpediente = expediente.detalleExpediente;

        props.onConfirm(row);
    };

    //funciones
    function getErrors() {

        const errors = {};

        if (formValues.idTipoRecargoDescuento <= 0) errors.idTipoRecargoDescuento = VALIDATION_MESSAGE.REQUIRED_OPTION;
        if (formValues.idTasa <= 0) errors.idTasa = VALIDATION_MESSAGE.REQUIRED_OPTION;
        if (formValues.idSubTasa <= 0) errors.idSubTasa = VALIDATION_MESSAGE.REQUIRED_OPTION;

        if (formValues.fechaDesde == null) errors.fechaDesde = VALIDATION_MESSAGE.REQUIRED_DATE;
        else if (tieneVigenciaDuplicada()) errors.fechaDesde = 'Ya hay uno del mismo tipo en esta vigencia';

        if (formValues.fechaHasta != null &&
            CompareDates(formValues.fechaHasta, formValues.fechaDesde) < 0) errors.fechaHasta = 'No puede ser anterior a la fecha desde';
        if (formValues.fechaOtorgamiento != null &&
            CompareDates(formValues.fechaOtorgamiento, today) > 0) errors.fechaOtorgamiento = 'No puede ser posterior a hoy';

        if (formValues.checkPorcentaje && (!isValidNumber(formValues.porcentaje, false) || formValues.porcentaje === 0)) errors.porcentaje = 'No puede ser cero';
        if (formValues.checkImporte && (!isValidNumber(formValues.importe, false) || formValues.importe === 0)) errors.importe = 'No puede ser cero';

        return errors;
    }

    function tieneVigenciaDuplicada() {

        const item = formValues;

        return listOthers.some(f =>
            f.idTipoRecargoDescuento === item.idTipoRecargoDescuento &&
            (f.idRubro ?? 0) === (item.idRubro ?? 0) &&
            f.idSubTasa === item.idSubTasa &&
            (isNull(item.fechaHasta) || f.fechaDesde <= item.fechaHasta) &&
            (isNull(f.fechaHasta) || item.fechaDesde <= f.fechaHasta)
        );
    }

    const getDescTipoRecargoDescuento = (id) => {
        const row = getRowEntidad('TipoRecargoDescuento', id);
        return (row) ? row.nombre : '';
    }

    return (
        <>

            <Modal
                size="lg"
                title={`Recargo/Descuento: ${(state.entity.id > 0) ? getDescTipoRecargoDescuento(state.entity.idTipoRecargoDescuento) : "Nuevo"}`}
                tipName={TIPS.NAME.MODALS.RECARGO_DESCUENTO_MODAL}
                onEscape={() => props.onDismiss()}
                body={(
                            <div className="row">

                                <div className="mb-3 col-12">
                                    <Accordion title="Recargo-Descuento" startOpen={true}>
                                            <div className='row form-basic'>
                                                <div className="mb-3 col-6">
                                                    <FormLabel htmlFor="idTipoRecargoDescuento" required>Tipo</FormLabel>
                                                    <InputEntidad
                                                        name="idTipoRecargoDescuento"
                                                        placeholder=""
                                                        tipName={TIPS.NAME.MODALS.TIPO_RECARGO_DESCUENTO_MODAL}
                                                        className={`form-control ${errorOf('idTipoRecargoDescuento') ? 'is-invalid' : ''}`}
                                                        value={formValues.idTipoRecargoDescuento}
                                                        onChange={({target}) => {
                                                            if (target.row) {
                                                                formSet({
                                                                    ...formValues,
                                                                    idTipoRecargoDescuento: target.row.id,
                                                                    importe: target.row.importe,
                                                                    porcentaje: target.row.porcentaje,
                                                                    checkImporte: (target.row.importe !== 0 && target.row.porcentaje === 0),
                                                                    checkPorcentaje: (target.row.importe === 0 && target.row.porcentaje !== 0),
                                                                });
                                                            } else {
                                                                formSet({
                                                                    ...formValues,
                                                                    idTipoRecargoDescuento: 0,
                                                                    importe: 0,
                                                                    porcentaje: 0,
                                                                    checkImporte: false,
                                                                    checkPorcentaje: false
                                                                });
                                                            }
                                                        }}
                                                        disabled={props.disabled}
                                                        title="Tipo Recargo Descuento"
                                                        entidad="TipoRecargoDescuento"
                                                        filter={row => (row.idTipoTributo === null || row.idTipoTributo === state.idTipoTributo)}
                                                        onFormat={row => (row) ? `${row.codigo} - ${row.nombre}` : ''}
                                                    />
                                                    <FormError message={errorOf('idTipoRecargoDescuento')} />
                                                </div>

                                                <div className="mb-3 col-6">
                                                    <label htmlFor="idRubro" className="form-label">Rubro</label>
                                                    <InputEntidad
                                                        name="idRubro"
                                                        placeholder=""
                                                        tipName={TIPS.NAME.MODALS.RUBRO_MODAL}
                                                        className="form-control"
                                                        value={formValues.idRubro}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                        title="Rubro"
                                                        entidad="Rubro"
                                                    />
                                                    <FormError />
                                                </div>

                                                <div className="mb-3 col-6">
                                                    <FormLabel htmlFor="idTasa" required>Tasa</FormLabel>
                                                    <InputTasa
                                                        name="idTasa"
                                                        placeholder=""
                                                        className={`form-control ${errorOf('idTasa') ? 'is-invalid' : ''}`}
                                                        value={formValues.idTasa}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                    />
                                                    <FormError message={errorOf('idTasa')} />
                                                </div>

                                                <div className="mb-3 col-6">
                                                    <FormLabel htmlFor="idSubTasa" required>Sub tasa</FormLabel>
                                                    <InputSubTasa
                                                        name="idSubTasa"
                                                        placeholder=""
                                                        className={`form-control ${errorOf('idSubTasa') ? 'is-invalid' : ''}`}
                                                        value={formValues.idSubTasa}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                        idTasa={formValues.idTasa}
                                                    />
                                                    <FormError message={errorOf('idSubTasa')} />
                                                </div>

                                                <div className="mb-3 col-4 col-md-3">
                                                    <FormLabel htmlFor="fechaDesde" required>Fecha
                                                        desde</FormLabel>
                                                    <DatePickerCustom
                                                        name="fechaDesde"
                                                        placeholder=""
                                                        className={`form-control ${errorOf('fechaDesde') ? 'is-invalid' : ''}`}
                                                        value={formValues.fechaDesde}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                    />
                                                    <FormError message={errorOf('fechaDesde')} />
                                                </div>

                                                <div className="mb-3 col-4 col-md-3">
                                                    <label htmlFor="fechaHasta" className="form-label">Fecha
                                                        hasta</label>
                                                    <DatePickerCustom
                                                        name="fechaHasta"
                                                        placeholder=""
                                                        className={`form-control ${errorOf('fechaHasta') ? 'is-invalid' : ''}`}
                                                        value={formValues.fechaHasta}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                        minValue={formValues.fechaDesde}
                                                    />
                                                    <FormError message={errorOf('fechaHasta')} />
                                                </div>

                                                <div className="mb-3 col-4 col-md-3">
                                                    <label htmlFor="fechaOtorgamiento" className="form-label">Fecha
                                                        otorgamiento</label>
                                                    <DatePickerCustom
                                                        name="fechaOtorgamiento"
                                                        placeholder=""
                                                        className={`form-control ${errorOf('fechaOtorgamiento') ? 'is-invalid' : ''}`}
                                                        value={formValues.fechaOtorgamiento}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                        maxValue={today}
                                                    />
                                                    <FormError message={errorOf('fechaOtorgamiento')} />
                                                </div>

                                                <div className="mb-3 col-4 col-md-3">
                                                    <label htmlFor="numeroSolicitud" className="form-label">Número de
                                                        solicitud</label>
                                                    <input
                                                        name="numeroSolicitud"
                                                        type="text"
                                                        placeholder=""
                                                        className="form-control"
                                                        value={formValues.numeroSolicitud}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                    />
                                                    <FormError />
                                                </div>

                                                <div className="mb-3 col-12 col-md-4">
                                                    <SegmentedControl
                                                        name="tipoValor"
                                                        label="Tipo de valor"
                                                        value={formValues.checkPorcentaje ? 'porcentaje' : (formValues.checkImporte ? 'importe' : '')}
                                                        options={[
                                                            { value: 'porcentaje', label: 'Porcentaje' },
                                                            { value: 'importe', label: 'Importe' },
                                                        ]}
                                                        onChange={({ target }) => {
                                                            const actual = formValues.checkPorcentaje ? 'porcentaje' : (formValues.checkImporte ? 'importe' : '');
                                                            if (target.value === actual) return;
                                                            formSet({
                                                                ...formValues,
                                                                checkPorcentaje: target.value === 'porcentaje',
                                                                checkImporte: target.value === 'importe',
                                                                importe: 0,
                                                                porcentaje: 0
                                                            });
                                                        }}
                                                        disabled={props.disabled}
                                                    />
                                                    <FormError />
                                                </div>

                                                <div className="mb-3 col-4 col-md-4">
                                                    <FormLabel htmlFor="porcentaje" required={formValues.checkPorcentaje}>Porcentaje</FormLabel>
                                                    <InputPercent
                                                        name="porcentaje"
                                                        placeholder=""
                                                        className={`form-control ${errorOf('porcentaje') ? 'is-invalid' : ''}`}
                                                        value={formValues.porcentaje}
                                                        allowNegative={true}
                                                        onChange={formHandle}
                                                        validation={(value) => value === undefined || (value >= -100 && value <= 100)}
                                                        disabled={props.disabled || formValues.checkImporte}
                                                    />
                                                    <FormError message={errorOf('porcentaje')} />
                                                </div>

                                                <div className="mb-3 col-4 col-md-4">
                                                    <FormLabel htmlFor="importe" required={formValues.checkImporte}>Importe</FormLabel>
                                                    <InputNumber
                                                        name="importe"
                                                        placeholder=""
                                                        className={`form-control ${errorOf('importe') ? 'is-invalid' : ''}`}
                                                        value={formValues.importe}
                                                        precision={2}
                                                        allowNegative={true}
                                                        onChange={formHandle}
                                                        disabled={props.disabled || formValues.checkPorcentaje}
                                                    />
                                                    <FormError message={errorOf('importe')} />
                                                </div>

                                                <div className="mb-3 col-12 col-md-6">
                                                    <label htmlFor="idPersona"
                                                           className="form-label">Solicitante</label>
                                                    <InputPersona
                                                        name="idPersona"
                                                        placeholder=""
                                                        className="form-control"
                                                        value={formValues.idPersona}
                                                        idTipoPersona={0}
                                                        personas={state.personas}
                                                        onChange={(event) => {
                                                            const {target} = event;
                                                            const persona = (target.row) ? {
                                                                idTipoPersona: target.row.idTipoPersona,
                                                                idTipoDocumento: target.row.idTipoDocumento,
                                                                numeroDocumento: target.row.numeroDocumento,
                                                                nombrePersona: target.row.nombrePersona
                                                            } : {
                                                                idTipoPersona: 0,
                                                                idTipoDocumento: 0,
                                                                numeroDocumento: "",
                                                                nombrePersona: ""
                                                            };
                                                            setPersona(persona);
                                                            formHandle(event);
                                                        }}
                                                        disabled={props.disabled}
                                                    />
                                                </div>
                                            </div>
                                    </Accordion>
                                </div>

                                <div className="mb-3 col-12">
                                    <Accordion title="Declaración jurada">
                                            <div className='row form-basic'>
                                                <div className="col-6 col-md-4">
                                                    <label htmlFor="numeroDDJJ" className="form-label">Número</label>
                                                    <input
                                                        name="numeroDDJJ"
                                                        type="text"
                                                        placeholder=""
                                                        className="form-control"
                                                        value={formValues.numeroDDJJ}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                    />
                                                </div>
                                                <div className="col-6 col-md-4">
                                                    <label htmlFor="letraDDJJ" className="form-label">Letra</label>
                                                    <input
                                                        name="letraDDJJ"
                                                        type="text"
                                                        placeholder=""
                                                        className="form-control"
                                                        value={formValues.letraDDJJ}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                    />
                                                </div>
                                                <div className="col-6 col-md-4">
                                                    <label htmlFor="ejercicioDDJJ" className="form-label">Año</label>
                                                    <InputEjercicio
                                                        name="ejercicioDDJJ"
                                                        placeholder=""
                                                        className="form-control"
                                                        value={formValues.ejercicioDDJJ}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                    />
                                                </div>
                                            </div>
                                    </Accordion>
                                </div>

                                <div className="mb-3 col-12">
                                    <Accordion title="Decreto/Ordenanza">
                                            <div className='row form-basic'>
                                                <div className="col-6 col-md-4">
                                                    <label htmlFor="numeroDecreto" className="form-label">Número</label>
                                                    <input
                                                        name="numeroDecreto"
                                                        type="text"
                                                        placeholder=""
                                                        className="form-control"
                                                        value={formValues.numeroDecreto}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                    />
                                                </div>
                                                <div className="col-6 col-md-4">
                                                    <label htmlFor="letraDecreto" className="form-label">Letra</label>
                                                    <input
                                                        name="letraDecreto"
                                                        type="text"
                                                        placeholder=""
                                                        className="form-control"
                                                        value={formValues.letraDecreto}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                    />
                                                </div>
                                                <div className="col-6 col-md-4">
                                                    <label htmlFor="ejercicioDecreto" className="form-label">Año</label>
                                                    <InputEjercicio
                                                        name="ejercicioDecreto"
                                                        placeholder=""
                                                        className="form-control"
                                                        value={formValues.ejercicioDecreto}
                                                        onChange={formHandle}
                                                        disabled={props.disabled}
                                                    />
                                                </div>
                                            </div>
                                    </Accordion>
                                </div>

                                <div className="mb-3 col-12">
                                   <Accordion title="Expediente">
                                       <div className='row form-basic'>
                                           <div className="col-12">
                                               <FormLabel htmlFor="idExpediente">Expediente</FormLabel>
                                               <InputExpediente
                                                   name="idExpediente"
                                                   placeholder=""
                                                   className={`form-control ${errorOf('idExpediente') ? 'is-invalid' : ''}`}
                                                   value={formValues.idExpediente}
                                                   onChange={(event) => {
                                                       const {target} = event;
                                                       const expediente = (target.row) ? {
                                                           detalleExpediente: target.row.detalleExpediente,
                                                       } : {
                                                           detalleExpediente: ""
                                                       };
                                                       setExpediente(expediente);
                                                       formHandle(event);
                                                   }}
                                                   disabled={props.disabled}
                                               />
                                               <FormError message={errorOf('idExpediente')} />
                                           </div>
                                       </div>
                                   </Accordion>
                                </div>

                                <div className="mb-3 col-12">
                                   <Accordion title="Información adicional">
                                       <DataTaggerFormRedux
                                           title="Información adicional de recargo/descuento"
                                           processKey={props.processKey}
                                           entidad="RecargoDescuento"
                                           idEntidad={state.entity.id}
                                           disabled={props.disabled}
                                       />
                                   </Accordion>
                                </div>

                            </div>
                )}
                footer={!props.disabled ? (<>
                                <button className="btn btn-outline-primary" data-dismiss="modal"
                                        onClick={(event) => props.onDismiss()}>Cancelar
                                </button>
                                <button className="btn btn-primary" data-dismiss="modal"
                                        onClick={(event) => handleClickAceptar()}>Aceptar
                                </button>
                </>) : (
                                <button className="btn back-button" data-dismiss="modal"
                                        onClick={(event) => props.onDismiss()}>Volver
                                </button>
                )}
                footerClassName={props.disabled ? 'f-align' : ''}
            />

        </>
    );
}

RecargoDescuentoModal.propTypes = {
    processKey: string.isRequired,
    disabled: bool,
    data: object.isRequired,
    onConfirm: func.isRequired,
    onDismiss: func.isRequired,
};

RecargoDescuentoModal.defaultProps = {
    disabled: false
};

export default RecargoDescuentoModal;
